import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MfaSetupPage } from '../auth/MfaSetupPage';
import { SignInPage } from '../auth/SignInPage';
import { useMe, useSession, WELCOME_KEY } from '../auth/session';
import { Beacon } from '../components/feedback/Beacon';
import { TopProgress } from '../components/feedback/Progress';
import { toast, Toaster } from '../components/feedback/toast';
import { Button } from '../components/ui/Button';
import { AppShell } from '../components/shell/AppShell';
import { ApiError } from '../lib/api';
import { HomePage } from '../pages/HomePage';
import { AppearancePage, NotFoundPage } from '../pages/misc';
import { OrganizationPage } from '../pages/OrganizationPage';
import { PeoplePage } from '../pages/PeoplePage';

// Routes. Guards are components: signed out → sign-in; MFA required and blocking → setup
// screen first (the API would answer 403 mfa_enrollment_required anyway).

function Root() {
  return (
    <>
      <TopProgress />
      <Outlet />
      <Toaster />
    </>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { status, signOut } = useSession();
  const me = useMe(status === 'signedIn');
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const error = me.error;
  // Only a definite "you're not allowed" ends the session; anything else (network, a database
  // waking up) keeps it and offers a retry instead of silently signing the person out.
  const denied = error instanceof ApiError && (error.status === 401 || error.status === 403);

  useEffect(() => {
    if (!denied) return;
    const key = error.status === 401 ? 'errors.unauthenticated' : 'auth.noAccess';
    void signOut().then(() => toast.error(t(key), { duration: 10_000 }));
  }, [denied, error, signOut, t]);

  // "Welcome back" once per sign-in, as soon as we know who it is.
  useEffect(() => {
    if (!me.data || sessionStorage.getItem(WELCOME_KEY) !== '1') return;
    sessionStorage.removeItem(WELCOME_KEY);
    toast.success(t('auth.welcome', { name: me.data.user.displayName.split(' ')[0] }));
  }, [me.data, t]);

  if (status === 'checking') return <Beacon />;
  if (status === 'signedOut') return <Navigate to="/sign-in" />;
  if (me.isLoading) return <Beacon />;
  if (denied) return <Beacon />;
  if (error)
    return <LoadFailed onRetry={() => void me.refetch()} onSignOut={() => void signOut()} />;
  if (me.data?.mfa.blocking && pathname !== '/security/mfa') return <Navigate to="/security/mfa" />;
  return <>{children}</>;
}

/** Profile couldn't load for a reason other than access: keep the session, offer a retry. */
function LoadFailed({ onRetry, onSignOut }: { onRetry: () => void; onSignOut: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-5">
      <div
        className="w-full max-w-md rounded-xl border border-border bg-surface p-8 text-center elevated"
        role="alert"
      >
        <h1 className="text-xl font-bold text-text">{t('errors.loadFailed.title')}</h1>
        <p className="mt-2 text-base text-muted">{t('errors.loadFailed.body')}</p>
        <div className="mt-6 flex justify-center gap-2">
          <Button variant="ghost" onClick={onSignOut}>
            {t('shell.user.signOut')}
          </Button>
          <Button onClick={onRetry}>{t('common.retry')}</Button>
        </div>
      </div>
    </div>
  );
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useSession();
  if (status === 'checking') return <Beacon />;
  if (status === 'signedIn') return <Navigate to="/" />;
  return <>{children}</>;
}

const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFoundPage });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: () => (
    <GuestOnly>
      <SignInPage />
    </GuestOnly>
  ),
});

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security/mfa',
  component: () => (
    <RequireAuth>
      <MfaSetupPage />
    </RequireAuth>
  ),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  ),
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });
const orgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization',
  component: OrganizationPage,
});
const peopleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/people',
  component: PeoplePage,
});
const appearanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/appearance',
  component: AppearancePage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  mfaRoute,
  appRoute.addChildren([homeRoute, orgRoute, peopleRoute, appearanceRoute]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/** Don't retry what retrying can't fix (4xx); do retry blips (network, 5xx) twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}
